import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import darkSelectStyles from "../../../shared/darkSelectStyles";

const FIELD_LABELS = { door_time: "Doors Time", start_time: "Event Start Time", end_time: "Event End Time" };
const FIELD_KEYS = ["door_time", "start_time", "end_time"];
const DAYS = [
  { wday: 0, short: "Su" }, { wday: 1, short: "Mo" }, { wday: 2, short: "Tu" },
  { wday: 3, short: "We" }, { wday: 4, short: "Th" }, { wday: 5, short: "Fr" }, { wday: 6, short: "Sa" }
];

const minuteToInput = (min) => {
  if (min == null) return "";
  const h = String(Math.floor(min / 60)).padStart(2, "0");
  const m = String(min % 60).padStart(2, "0");
  return `${h}:${m}`;
};
const inputToMinute = (val) => {
  if (!val) return null;
  const [h, m] = val.split(":").map(Number);
  return h * 60 + m;
};
let ruleKeySeq = 1;
const withKeys = (rules) => (rules || []).map(r => ({ key: ruleKeySeq++, days: r.days || [], times: { ...r.times } }));

const EventTimeDefaults = ({ teamId, initialOffsets, eventTimeDefaults, defaultUnits, defaultMinutes, defaultHours }) => {
  const [offsets, setOffsets] = useState(initialOffsets);
  const [defaultStartMinute, setDefaultStartMinute] = useState(
    eventTimeDefaults && eventTimeDefaults.default_start_minute != null ? eventTimeDefaults.default_start_minute : null
  );
  const [rules, setRules] = useState(withKeys(eventTimeDefaults && eventTimeDefaults.day_rules));

  const initialStart = eventTimeDefaults && eventTimeDefaults.default_start_minute != null ? eventTimeDefaults.default_start_minute : null;
  const lastSavedRef = useRef({ offsets: initialOffsets, start: initialStart, rules: (eventTimeDefaults && eventTimeDefaults.day_rules) || [] });

  const minuteOptions = defaultMinutes.map(m => ({ value: m, label: m }));
  const hourOptions = defaultHours.map(h => ({ value: h, label: h }));
  const unitOptions = defaultUnits.map(u => ({ value: u, label: u.charAt(0).toUpperCase() + u.slice(1) }));
  const unitValues = (unit) => (unit === "minutes" ? minuteOptions : hourOptions);

  const save = (nextOffsets, nextStart, nextRules) => {
    const savedRules = (nextRules || []).filter(r => r.days.length > 0 && Object.keys(r.times || {}).length > 0).map(r => ({ days: r.days, times: r.times }));
    client.patch(`/teams/${teamId}`, {
      team: {
        show_activity_defaults_attributes: nextOffsets,
        event_time_defaults: { default_start_minute: nextStart, day_rules: savedRules }
      }
    })
      .then(() => {
        lastSavedRef.current = { offsets: nextOffsets, start: nextStart, rules: savedRules };
        toastSuccess("Event time defaults updated");
      })
      .catch(() => {
        const last = lastSavedRef.current;
        setOffsets(last.offsets);
        setDefaultStartMinute(last.start);
        setRules(withKeys(last.rules));
        toastError("An error occurred");
      });
  };

  const debounceRef = useRef(null);
  const pendingRef = useRef(null);
  const debouncedSave = (nextOffsets, nextStart, nextRules) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingRef.current = [nextOffsets, nextStart, nextRules];
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      pendingRef.current = null;
      save(nextOffsets, nextStart, nextRules);
    }, 500);
  };
  const immediateSave = (nextOffsets, nextStart, nextRules) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    pendingRef.current = null;
    save(nextOffsets, nextStart, nextRules);
  };

  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      if (pendingRef.current) { save(...pendingRef.current); }
    }
  }, []);

  const saveOffsets = (next) => { setOffsets(next); immediateSave(next, defaultStartMinute, rules); };
  const saveStart = (next) => { setDefaultStartMinute(next); debouncedSave(offsets, next, rules); };
  const saveRules = (next) => { setRules(next); immediateSave(offsets, defaultStartMinute, next); };
  const saveRuleTimes = (next) => { setRules(next); debouncedSave(offsets, defaultStartMinute, next); };

  const toggleDay = (key, wday) => saveRules(rules.map(r => {
    if (r.key !== key) return r;
    const has = r.days.includes(wday);
    return { ...r, days: has ? r.days.filter(d => d !== wday) : [...r.days, wday] };
  }));
  const addRule = () => saveRules([...rules, { key: ruleKeySeq++, days: [], times: { door_time: 20 * 60 } }]);
  const removeRule = (key) => saveRules(rules.filter(r => r.key !== key));
  const setFieldTime = (key, field, minute) => saveRuleTimes(rules.map(r => r.key === key ? { ...r, times: { ...r.times, [field]: minute } } : r));
  const addField = (key) => saveRules(rules.map(r => {
    if (r.key !== key) return r;
    const next = FIELD_KEYS.find(f => !(f in r.times)) || "end_time";
    const def = next === "door_time" ? 20 * 60 : next === "start_time" ? 21 * 60 : 23 * 60;
    return { ...r, times: { ...r.times, [next]: def } };
  }));
  const removeField = (key, field) => saveRules(rules.map(r => {
    if (r.key !== key) return r;
    const times = { ...r.times }; delete times[field];
    return { ...r, times };
  }));

  const offsetRow = (idx, label, tenseLabel) => (
    <div className="d-flex flex-column flex-lg-row align-items-lg-center mt-3">
      <div className="mb-2 mb-lg-0 pr-3 font-weight-bold" style={{ width: "175px", whiteSpace: "nowrap" }}>
        <i className="far fa-calendar pr-2 text-muted" style={{ fontSize: "14px" }}></i>{label}
      </div>
      <div className="d-flex align-items-center">
        <div className="d-flex align-items-center mr-3 p-2 rounded" style={{ backgroundColor: "var(--sl-bg-tertiary, #f2f2f2)" }}>
          <div className="pr-2">@</div>
          <div className="pr-2" style={{ width: "100px" }}>
            <Select
              options={unitValues(offsets[idx].interval_unit)}
              menuPortalTarget={document.querySelector("body")}
              styles={darkSelectStyles}
              value={unitValues(offsets[idx].interval_unit).find(uv => uv.value === offsets[idx].interval_value)}
              onChange={(opt) => saveOffsets(offsets.map((o, i) => i === idx ? { ...o, interval_value: opt.value } : o))}
              components={{ IndicatorSeparator: () => null }}
            />
          </div>
          <div className="pr-2" style={{ width: "110px" }}>
            <Select
              options={unitOptions}
              menuPortalTarget={document.querySelector("body")}
              styles={darkSelectStyles}
              value={unitOptions.find(u => u.value === offsets[idx].interval_unit)}
              onChange={(opt) => {
                const changed = { ...offsets[idx], interval_unit: opt.value };
                if (!unitValues(opt.value).find(v => v.value === offsets[idx].interval_value)) {
                  changed.interval_value = unitValues(opt.value)[0].value;
                }
                saveOffsets(offsets.map((o, i) => i === idx ? changed : o));
              }}
              components={{ IndicatorSeparator: () => null }}
            />
          </div>
          <div style={{ width: "50px" }}>{tenseLabel}</div>
        </div>
        <div className="font-weight-bold">Event Start Time</div>
      </div>
    </div>
  );

  return (
    <div>
      <p className="mb-1 text-dark" style={{ fontSize: "16px" }}><strong>Default times</strong></p>
      <p className="m-0 small text-muted">Used whenever no day-specific rule applies. Doors and End Time are calculated from the event start time.</p>

      <div className="d-flex align-items-center mt-3">
        <div className="pr-3 font-weight-bold" style={{ width: "175px", whiteSpace: "nowrap" }}>
          <i className="far fa-calendar pr-2 text-muted" style={{ fontSize: "14px" }}></i>Event Start Time
        </div>
        <input
          type="time"
          value={minuteToInput(defaultStartMinute)}
          onChange={(e) => saveStart(inputToMinute(e.target.value))}
          style={{ padding: "6px 10px", border: "1px solid var(--sl-border-primary, #d5d7da)", borderRadius: 8, backgroundColor: "var(--sl-bg-primary, #fff)", color: "var(--sl-text-primary, #181d27)" }}
        />
        <span className="ml-3 text-muted small">Default for new events and confirms</span>
      </div>

      {offsetRow(0, "Doors Time", "Before")}
      {offsetRow(1, "Event End Time", "After")}

      <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid var(--sl-border-secondary, #e9eaeb)" }} />

      <div className="d-flex align-items-start justify-content-between">
        <div>
          <p className="mb-1 text-dark" style={{ fontSize: "16px" }}><strong>Day-specific times</strong></p>
          <p className="m-0 small text-muted">Pin exact times for certain days of the week — like Doors always at 8:00 PM on weekends.</p>
        </div>
        <button className="btn btn-sm btn-outline-primary flex-shrink-0" onClick={addRule}>
          <i className="far fa-plus mr-1"></i>Add day rule
        </button>
      </div>

      <p className="small mt-3 mb-0" style={{ color: "var(--sl-text-brand, #136395)" }}>
        Day-specific times take priority over your defaults. They pre-fill when you confirm an event on a matching day, and you can still edit them per event.
      </p>

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        {rules.length === 0 && (
          <div style={{ textAlign: "center", padding: "28px 16px", border: "1px dashed var(--sl-border-primary, #d5d7da)", borderRadius: 10, background: "var(--sl-bg-secondary, #fafafa)" }}>
            <p className="m-0 font-weight-bold" style={{ color: "var(--sl-text-primary, #414651)" }}>No day-specific times yet</p>
            <p className="m-0 text-muted">Add a rule to pin Doors, Start, or End times for specific days.</p>
          </div>
        )}

        {rules.map((rule) => (
          <div key={rule.key} style={{ border: "1px solid var(--sl-border-secondary, #e9eaeb)", borderRadius: 10 }}>
            <div className="d-flex align-items-center justify-content-between" style={{ padding: "14px 16px", borderBottom: "1px solid var(--sl-border-secondary, #f5f5f5)" }}>
              <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
                <span className="small font-weight-bold text-muted">Applies to</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {DAYS.map(({ wday, short }) => {
                    const on = rule.days.includes(wday);
                    return (
                      <button
                        key={wday}
                        onClick={() => toggleDay(rule.key, wday)}
                        style={{
                          width: 40, height: 40, borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
                          border: on ? "1px solid var(--sl-fg-brand, #1982c4)" : "1px solid var(--sl-border-primary, #d5d7da)",
                          background: on ? "var(--sl-fg-brand, #1982c4)" : "var(--sl-bg-primary, #fff)",
                          color: on ? "#fff" : "var(--sl-text-secondary, #535862)"
                        }}
                      >{short}</button>
                    );
                  })}
                </div>
              </div>
              <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); removeRule(rule.key); }} title="Remove rule">
                <i className="fas fa-trash"></i>
              </a>
            </div>

            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              {FIELD_KEYS.filter(f => f in rule.times).map((field) => (
                <div key={field} className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
                  <span style={{ width: 160, fontWeight: 600, color: "var(--sl-text-primary, #414651)" }}>{FIELD_LABELS[field]}</span>
                  <span className="text-muted">set to</span>
                  <input
                    type="time"
                    value={minuteToInput(rule.times[field])}
                    onChange={(e) => setFieldTime(rule.key, field, inputToMinute(e.target.value))}
                    style={{ padding: "6px 10px", border: "1px solid var(--sl-border-primary, #d5d7da)", borderRadius: 8, backgroundColor: "var(--sl-bg-primary, #fff)", color: "var(--sl-text-primary, #181d27)" }}
                  />
                  <a href="#" className="text-muted ml-auto" onClick={(e) => { e.preventDefault(); removeField(rule.key, field); }} title="Remove time">
                    <i className="fas fa-times"></i>
                  </a>
                </div>
              ))}
              {FIELD_KEYS.some(f => !(f in rule.times)) && (
                <div>
                  <a href="#" style={{ color: "var(--sl-text-brand, #136395)", fontWeight: 600 }} onClick={(e) => { e.preventDefault(); addField(rule.key); }}>
                    <i className="far fa-plus mr-1"></i>Add a time
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventTimeDefaults;
